import { OperatorType } from 'angular-slickgrid';
import { BaseRangeFilter, ModeOption } from './base-range-filter';
import { calculatePeriod } from './utils/period-calculator';
import { PERIOD_OPTIONS, PeriodOption } from './utils/period-options';
import { formatRangeDisplayValue } from './utils/range-display';
import { buildRangeSearchTerms } from './utils/range-terms';
import {
  areIsoDatesEqual,
  formatDateDisplayValue,
  formatInputToIso,
  formatIsoToInput,
  parseInputDateValue,
} from './utils/date-value';
import { getRangeModeOptions } from './utils/range-modes';
import { getEqualLabelKeyForType } from './utils/range-labels';
import { localizeWithFallback } from '../../utils/localization-utils';

export class CustomDateRangeFilter extends BaseRangeFilter<Date> {
  private equalInputElm!: HTMLInputElement;
  private fromInputElm!: HTMLInputElement;
  private toInputElm!: HTMLInputElement;
  private periodSelectElm!: HTMLInputElement;
  private includeTime: boolean = false;
  private periodOptions: PeriodOption[] = [];

  override init(args: any): void {
    if (args?.columnDef?.filter?.params) {
      this.includeTime = (args.columnDef.filter.params as any).includeTime ?? false;
    }

    super.init(args);
  }

  protected getModeOptions(): ModeOption[] {
    return getRangeModeOptions(true).map((mode, index) => ({
      id: mode.id,
      label: localizeWithFallback(this.localizationService, mode.labelKey),
      default: index == 0,
    }));
  }

  override clear(shouldTriggerQuery = true): void {
    if (this.equalInputElm) {
      this.equalInputElm.value = '';
      this.toggleInputFilledClass(this.equalInputElm);
    }
    if (this.fromInputElm) {
      this.fromInputElm.value = '';
      this.toggleInputFilledClass(this.fromInputElm);
    }
    if (this.toInputElm) {
      this.toInputElm.value = '';
      this.toggleInputFilledClass(this.toInputElm);
    }
    if (this.periodSelectElm) {
      this.periodSelectElm.checked = false;
    }

    super.clear(shouldTriggerQuery);
  }

  protected createInputsForMode(mode: string): HTMLElement {
    const container = document.createElement('div');
    const inputType = this.includeTime ? 'datetime-local' : 'date';

    if (mode === 'equal') {
      this.equalInputElm = document.createElement('input');
      this.equalInputElm.type = inputType;
      this.equalInputElm.className = 'form-control equal-input';

      const equalLabel = document.createElement('label');
      equalLabel.className = 'input-label';
      equalLabel.textContent =
        localizeWithFallback(this.localizationService, getEqualLabelKeyForType('date')) ||
        getEqualLabelKeyForType('date');

      this.equalInputElm.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.applyButtonElm.click();
      });
      this.equalInputElm.addEventListener('input', () => {
        this.toggleInputFilledClass(this.equalInputElm);
        this.updateApplyButtonState();
      });

      container.appendChild(equalLabel);
      container.appendChild(this.equalInputElm);
    } else if (mode === 'range') {
      container.className = 'between-inputs';

      const fromContainer = document.createElement('div');
      fromContainer.className = '';
      const fromLabel = document.createElement('label');
      fromLabel.className = 'input-label';
      fromLabel.textContent = localizeWithFallback(this.localizationService, 'LanguageService::From');
      this.fromInputElm = document.createElement('input');
      this.fromInputElm.type = inputType;
      this.fromInputElm.className = 'form-control from-input';
      fromContainer.appendChild(fromLabel);
      fromContainer.appendChild(this.fromInputElm);

      const toContainer = document.createElement('div');
      toContainer.className = '';
      const toLabel = document.createElement('label');
      toLabel.className = 'input-label';
      toLabel.textContent = localizeWithFallback(this.localizationService, 'LanguageService::To');
      this.toInputElm = document.createElement('input');
      this.toInputElm.type = inputType;
      this.toInputElm.className = 'form-control to-input';
      toContainer.appendChild(toLabel);
      toContainer.appendChild(this.toInputElm);

      this.fromInputElm.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.applyButtonElm.click();
      });
      this.toInputElm.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.applyButtonElm.click();
      });
      this.fromInputElm.addEventListener('input', () => {
        this.toggleInputFilledClass(this.fromInputElm);
        this.updateApplyButtonState();
      });
      this.toInputElm.addEventListener('input', () => {
        this.toggleInputFilledClass(this.toInputElm);
        this.updateApplyButtonState();
      });

      container.appendChild(fromContainer);
      container.appendChild(toContainer);
    } else if (mode === 'periods') {
      const radioContainer = document.createElement('div');
      radioContainer.className = 'period-radio-group';

      this.periodOptions = PERIOD_OPTIONS.map(period => ({
        value: period.value,
        labelKey: period.labelKey,
      }));

      this.periodOptions.forEach((period, index) => {
        if (period.value === '') return;

        const radioLabel = document.createElement('label');
        radioLabel.className = 'period-radio-label';

        const radioInput = document.createElement('input');
        radioInput.type = 'radio';
        radioInput.name = 'period-select';
        radioInput.value = period.value;
        radioInput.className = 'period-radio';

        radioInput.addEventListener('change', () => {
          this.periodSelectElm = radioInput;

          radioContainer
            .querySelectorAll('.period-radio-label')
            .forEach(lbl => lbl.classList.remove('selected'));

          radioLabel.classList.add('selected');
        });

        radioLabel.appendChild(radioInput);
        radioLabel.appendChild(
          document.createTextNode(localizeWithFallback(this.localizationService, period.labelKey))
        );
        radioContainer.appendChild(radioLabel);
      });

      container.appendChild(radioContainer);
    }
    return container;
  }

  protected areInputsAvailable(): boolean {
    if (this.currentMode === 'equal') {
      return this.equalInputElm != null;
    } else if (this.currentMode === 'range') {
      return this.fromInputElm != null && this.toInputElm != null;
    } else if (this.currentMode === 'periods') {
      return this.periodSelectElm != null;
    }
    return false;
  }

  protected getValuesFromInputs(): any[] {
    if (!this.areInputsAvailable()) {
      return this.searchTerms;
    }

    if (this.currentMode === 'equal') {
      const equalValue = this.equalInputElm?.value;

      if (!equalValue) {
        return [];
      }

      const date = new Date(equalValue);

      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      return [
        formatInputToIso(startOfDay.toISOString(), true),
        formatInputToIso(endOfDay.toISOString(), true),
      ];
    }

    return buildRangeSearchTerms(
      this.currentMode as 'range' | 'periods',
      {
        from: this.fromInputElm?.value,
        to: this.toInputElm?.value,
        period: this.periodSelectElm?.value,
      },
      {
        formatValue: value => formatInputToIso(value, this.includeTime),
        periodToRange: period => calculatePeriod(period, this.includeTime),
      }
    );
  }

  protected setValuesInInputs(values: any[]): void {
    if (!values || values.length === 0) {
      return;
    }

    const targetMode = values.length === 1 ? 'equal' : 'range';

    if (this.currentMode !== targetMode) {
      this.currentMode = targetMode;
      this.updateModeSelection();
      this.updateInputsForMode();
    }

    setTimeout(() => {
      this.applyValuesToInputs(values, targetMode);
    }, 0);
  }

  private applyValuesToInputs(values: any[], mode: string): void {
    if (mode === 'equal' && values.length === 1) {
      const inputValue = formatIsoToInput(values[0], this.includeTime);
      if (this.equalInputElm && inputValue) {
        this.equalInputElm.value = inputValue;
        this.toggleInputFilledClass(this.equalInputElm);
      }
    } else if (mode === 'range' && values.length === 2) {
      const fromInput = values[0]
        ? formatIsoToInput(values[0], this.includeTime)
        : null;
      if (this.fromInputElm && fromInput) {
        this.fromInputElm.value = fromInput;
        this.toggleInputFilledClass(this.fromInputElm);
      }
      const toInput = values[1] ? formatIsoToInput(values[1], this.includeTime) : null;
      if (this.toInputElm && toInput) {
        this.toInputElm.value = toInput;
        this.toggleInputFilledClass(this.toInputElm);
      }
    }
    this.updateApplyButtonState();
  }
  
  protected updateApplyButtonState(): void {
    if (!this.applyButtonElm) return;

    let isValid = false;
    let errorMessage = '';

    if (this.currentMode === 'equal') {
      const v = this.equalInputElm?.value?.trim() || '';
      if (!v) {
        errorMessage = '';
      } else {
        const d = parseInputDateValue(v, this.includeTime);
        if (!d) {
          errorMessage = localizeWithFallback(this.localizationService, 
            'LanguageService::EnterValidDate'
          );
        } else {
          isValid = true;
        }
      }
    } else if (this.currentMode === 'range') {
      const fromVal = this.fromInputElm?.value?.trim() || '';
      const toVal = this.toInputElm?.value?.trim() || '';

      if (!fromVal && !toVal) {
        errorMessage = '';
      } else {
        const fromDt = fromVal ? parseInputDateValue(fromVal, this.includeTime) : undefined;
        const toDt = toVal ? parseInputDateValue(toVal, this.includeTime) : undefined;

        if (fromVal && !fromDt) {
          errorMessage = localizeWithFallback(this.localizationService, 
            'LanguageService::FromValidDate'
          );
        } else if (toVal && !toDt) {
          errorMessage = localizeWithFallback(this.localizationService, 
            'LanguageService::UntilValidDate'
          );
        } else if (fromDt && toDt && fromDt.getTime() > toDt.getTime()) {
          errorMessage = localizeWithFallback(this.localizationService, 
            'LanguageService::FromToValidDate'
          );
        } else {
          isValid = true;
        }
      }
    } else {
      isValid = true;
    }

    this.applyButtonElm.disabled = !isValid;
    if (this.errorElm) {
      this.errorElm.textContent = errorMessage;
    }
  }
  protected onModeChanged(): void {
    this.updateApplyButtonState();
  }

  protected formatDisplayValue(values: any[]): string {
    let result = '';

    if (!values || values.length === 0) {
      this.toggleHeaderFilledClass(false);
      return '';
    }

    if (this.currentMode === 'equal' && values.length === 1) {
      result = formatRangeDisplayValue(
        'equal',
        { equal: values[0] },
        value => formatDateDisplayValue(value),
        { compareValues: areIsoDatesEqual }
      );
    } else if (
      (this.currentMode === 'range' || this.currentMode === 'periods') &&
      values.length === 2
    ) {
      result = formatRangeDisplayValue(
        'range',
        { from: values[0], to: values[1] },
        value => formatDateDisplayValue(value),
        { compareValues: areIsoDatesEqual }
      );
    }

    this.toggleHeaderFilledClass(result.trim() !== '');
    return result;
  }
  private toggleInputFilledClass(input: HTMLInputElement): void {
    if (input.value.trim() !== '') {
      input.classList.add('filled');
    } else {
      input.classList.remove('filled');
    }
  }

  protected getOperatorForMode(mode: string): OperatorType {
    return OperatorType.rangeInclusive;
  }

  private toggleHeaderFilledClass(isFilled: boolean): void {
    const headerInput = this.headerInputElm;
    if (!headerInput) return;

    if (isFilled) {
      headerInput.classList.add('filled');
    } else {
      headerInput.classList.remove('filled');
    }
  }
}
